import { Box } from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import { ReactComponent as Bell } from "assets/Bell.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import NoPlansBanner from "components/common/NoPlansBanner";
import Typography from "components/common/Typography";
import Item from "components/common/plans/item";
import { triggerOnboadingWizard } from "components/features/Onboarding/Helper";
import { useTranslate } from "hooks/useTranslate";
import { uniqueId } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes, allPlansType } from "utils/CommonConstants";
import { pageNames } from "utils/Types";
import "../../../../../src/index.scss";
import { SuggestedplansIds } from "./SuggestedPlans";
import { fetchSuggestedPlansAsync, setSuggestedPlanList } from "./SuggestedPlans.slice";
import "./index.scss";

interface props {
  showFocusAreas?: boolean;
  filters?: any;
  showNoSuggestedBanner?: boolean;
  suggestedPlanIds: SuggestedplansIds[];
}

const SuggestedPlans = (props: props) => {
  const { showFocusAreas, filters = {}, showNoSuggestedBanner = false, suggestedPlanIds } = props;

  const translate = useTranslate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isSeeAll, setIsSeeAll] = useState<boolean>(false);
  const [seeAllSuggestions, setSeeAllSuggestions] = useState<boolean>(location?.state?.showSeeAll ?? false);

  const [focusedItems, setFocusedItems] = useState<string[] | []>([]);

  const plansList: any = useAppSelector((state) => state.suggestedPlans.SuggestedPlansList);

  const innCode = useAppSelector((state: any) => state.app.innCode);
  const newAreaOfFocusesAvailable: any = useAppSelector((state) => state.suggestedPlans.newAreaOfFocusesAvailable);
  const selectedFocusAreas: string[] = useAppSelector((state) => state.suggestedPlans.selectedFocusAreas);
  const { isMobileScreen } = useContext(ThemeContext);
  const suggestedPlansList = !seeAllSuggestions ? plansList?.slice(0, 3) : plansList;
  const showSeeAllButton = plansList?.length > 3;

  const getSuggestedPlans = async () => {
    const ids = suggestedPlanIds?.map((item: any) => item?.cmsId);
    let props: any = {
      filter: {
        innCode,
        ids: ids,
        ...filters?.filtersRequest
      },
      planType: filters?.filtersRequest["planType"]?.length > 0 ? filters?.filtersRequest["planType"] : [allPlansType],
      pagination: {
        page: 1,
        size: ids?.length
      },
      isSortingDisabled: true,
      myPlansDisabled: true
    };
    if (filters?.functionalAreaIds?.length)
      props.filter = { ...props.filter, functionalAreaIds: filters?.functionalAreaIds };
    if (filters.searchText) props = { ...props, searchText: filters?.searchText };
    dispatch(fetchSuggestedPlansAsync(props));
  };

  useEffect(() => {
    if (suggestedPlanIds?.length) getSuggestedPlans();
    else dispatch(setSuggestedPlanList());
  }, [suggestedPlanIds]);

  const handleSetSeeAll = () => {
    setIsSeeAll(!isSeeAll);
  };

  const handleSeeAllSuggestions = () => {
    setSeeAllSuggestions(!seeAllSuggestions);
  };

  useEffect(() => {
    const subset = selectedFocusAreas?.slice(0, 3);
    if (!isSeeAll) {
      setFocusedItems(subset);
    } else {
      setFocusedItems(selectedFocusAreas);
    }
  }, [isSeeAll, selectedFocusAreas]);

  const focusedAreaText = isMobileScreen
    ? translate("dashboard.focusedArea.flagTrue.mobile")
    : translate("dashboard.focusedArea.flagTrue.desktop");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {suggestedPlansList && suggestedPlansList?.length > 0 && showFocusAreas && (
        <div className="banner-main">
          <div className="left-container">
            <div className="heading-wrapper">
              {newAreaOfFocusesAvailable && (
                <>
                  <Bell />
                  <Typography showBold variant="h5" color={tokens.Icon.General.Inverse} children={focusedAreaText} />
                  <Bell />
                </>
              )}
            </div>
            {selectedFocusAreas?.length ? (
              <>
                <div className="content-wrapper">
                  <Typography variant="body2" color={tokens.Icon.General.Inverse} className="suggestion-subHeading">
                    <div className="p-r-2">
                      Retake the Wizard to select additional focus areas. You are seeing suggestions for{" "}
                    </div>
                    <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                      "
                    </Typography>
                    {focusedItems?.length > 0 &&
                      focusedItems?.map((data, i: number) => {
                        return (
                          <div className="inner-wrapper" key={uniqueId("ITEM_")}>
                            <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                              {data}
                            </Typography>
                            {selectedFocusAreas?.length - 1 !== i && (
                              <Typography variant="body2" showBold color={tokens.Icon.General.Inverse} className="ml-4">
                                /
                              </Typography>
                            )}
                          </div>
                        );
                      })}
                    {selectedFocusAreas?.length > 3 && !isSeeAll && (
                      <Typography variant="body2" showBold color={tokens.Icon.General.Inverse} className="dots">
                        (...)
                      </Typography>
                    )}
                    <Typography variant="body2" showBold color={tokens.Icon.General.Inverse} className="dots">
                      "
                    </Typography>
                    <Typography variant="body2" showBold color={tokens.Icon.General.Inverse}>
                      .
                    </Typography>
                    {!isSeeAll && selectedFocusAreas?.length > 3 && (
                      <Typography
                        variant="body2"
                        showBold
                        color={tokens.Icon.General.Inverse}
                        className="see-more"
                        onClick={() => handleSetSeeAll()}
                      >
                        {translate("common.seeMore")}
                      </Typography>
                    )}
                  </Typography>
                </div>
                {isSeeAll && (
                  <Typography
                    variant="body2"
                    color={tokens.Icon.General.Inverse}
                    showBold
                    className="see-less"
                    onClick={() => handleSetSeeAll()}
                  >
                    {translate("common.seeLessUpper")}
                  </Typography>
                )}
              </>
            ) : (
              <Typography
                showBold
                variant="body1"
                color={tokens.Icon.General.Inverse}
                children="No focus areas selected"
              />
            )}
          </div>
          <div className="right-container">
            <Button
              accessType={accessTypes.delete}
              variant="primaryWhite"
              onClick={() => {
                triggerOnboadingWizard(dispatch);
              }}
              label={"Retake the Wizard"}
            />
          </div>
        </div>
      )}
      {suggestedPlansList && suggestedPlansList?.length > 0 ? (
        <Box
          sx={{
            backgroundColor: tokens.Background.General.Tertiary,
            borderRadius: isMobileScreen ? "0" : "12px",
            marginX: isMobileScreen ? "-16px" : "0",
            padding: "16px"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ width: isMobileScreen ? "70%" : "inherit" }}>
              <Typography variant={"h"} showBold color={tokens.Text.General.PrimaryHeadline}>
                Suggested Plans
              </Typography>
              <Box maxWidth={"450px"} marginY="16px">
                <Typography variant={"body2"} color={tokens.Text.General.Secondary}>
                  These are the plans that we believe will really boost your results. However further down the page you
                  can see other suggestions
                </Typography>
              </Box>
            </div>
            {showSeeAllButton && (
              <div onClick={handleSeeAllSuggestions} className="cursor-pointer" style={{ display: "flex", gap: "4px" }}>
                <Typography variant="body2" color={tokens.Icon.General.Secondary} component="span">
                  {!seeAllSuggestions ? "See All" : "See Less"}
                </Typography>
                <Box style={{ display: "flex", marginTop: "8px" }}>
                  {" "}
                  {!seeAllSuggestions ? <ArrowDown /> : <ArrowUp />}
                </Box>
              </div>
            )}
          </div>

          {suggestedPlansList?.map((item: any, index: number) => {
            return (
              <>
                {" "}
                <Item
                  key={item.id}
                  suggested={true}
                  item={item}
                  isBookmarked={item?.isBookmarked}
                  isSuggestedPannel={true}
                ></Item>
                {!seeAllSuggestions && showSeeAllButton && index === 2 && (
                  <div>
                    {" "}
                    <Button width="100%" onClick={handleSeeAllSuggestions} label={"See All Suggestions"} />
                  </div>
                )}
              </>
            );
          })}
        </Box>
      ) : (
        <>
          {showNoSuggestedBanner && (
            <NoPlansBanner
              // sx={{ minHeight: "180px", maxHeight: isMobileScreen ? "320px" : "270px" }}
              minHeight="180px"
              maxHeight={isMobileScreen ? "" : "290px"}
              showNoSuggestedBanner={showNoSuggestedBanner}
              pageName={pageNames.suggested}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SuggestedPlans;
